import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import CouponUnlockCodes from "@saleor/coupons/components/CouponUnlockCodes/CouponUnlockCodes";
import useUnlockCodesState from "@saleor/coupons/components/CouponUnlockCodes/useUnlockCodesState";
import DiscardWarnDialog from "@saleor/coupons/components/DiscardWarnDialog/DiscardWarnDialog";
import UnlockCodesHeader from "@saleor/coupons/components/UnlockCodesHeader/UnlockCodesHeader";
import { couponUnlockCodeBulkCreate } from "@saleor/coupons/mutations";
import { useCouponDetailsForSetCodesQuery } from "@saleor/coupons/queries";
import {
  CouponUnlockCodeBulkCreate,
  CouponUnlockCodeBulkCreateVariables
} from "@saleor/coupons/types/CouponUnlockCodeBulkCreate";
import { couponUnlockCodeBulkUpload_couponUnlockCodeBulkUploadAndExtract_unlockCodes } from "@saleor/coupons/types/couponUnlockCodeBulkUpload";
import { couponUrl } from "@saleor/coupons/url";
import { extractAlreadyExistsUnlockCodeError } from "@saleor/coupons/utils";
import useAppState from "@saleor/hooks/useAppState";
import useNotifier from "@saleor/hooks/useNotifier";
import { CouponUnlockCodeBulkCreateInput } from "@saleor/types/globalTypes";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-apollo";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { SAVE_BUTTON_BAR_LABELS } from "./constants";
import useStyles from "./styles";

interface ICouponAddUnlockCodePageProps {
  couponId: string;
}

const CouponAddUnlockCodePage: FC<ICouponAddUnlockCodePageProps> = ({
  couponId
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const classes = useStyles({});
  const [isDiscardWarnShown, setIsDiscardWarnShown] = useState(false);
  const [navigateToUrlStr, setNavigateToUrlStr] = useState("");
  const [, dispatchAppState] = useAppState();
  const {
    unlockCodeState,
    unlockCodeStateDispatcher,
    onInsertCallback,
    setExistingUnlockCodesError,
    getUnlockCodeObjErrorFn
  } = useUnlockCodesState();

  const navigate = useNavigate();
  const location = useLocation();

  const { data: couponData } = useCouponDetailsForSetCodesQuery({
    displayLoader: true,
    variables: { id: couponId }
  });

  const [
    unlockCodeCreateMutation,
    { loading: isBulkCreateMutationLoading }
  ] = useMutation<
    CouponUnlockCodeBulkCreate,
    CouponUnlockCodeBulkCreateVariables
  >(couponUnlockCodeBulkCreate);

  useEffect(() => {
    const locationState: {
      unlockCodes?: couponUnlockCodeBulkUpload_couponUnlockCodeBulkUploadAndExtract_unlockCodes[];
    } = location.state;
    if (
      location.state !== null &&
      Object.keys(locationState).includes("unlockCodes")
    ) {
      unlockCodeStateDispatcher({
        type: "bulk-add-unlock-codes",
        unlockCodes: locationState.unlockCodes
      });
    }
  }, []);

  const handleInsert = useCallback(() => {
    const createMutationPayload: CouponUnlockCodeBulkCreateInput[] = unlockCodeState.listArr.map(
      el => ({
        code: el.unlockCode,
        coupon: couponId,
        description: el.description,
        tags: el.couponReportTags,
        usageLimit: el.unlockCodeUsageLimit
      })
    );
    onInsertCallback(() => {
      dispatchAppState({
        payload: {
          value: true
        },
        type: "displayLoader"
      });
      unlockCodeCreateMutation({
        variables: { input: createMutationPayload }
      })
        .then(data => {
          if (data.data.couponUnlockCodeBulkCreate.errors.length > 0) {
            // Checks for Duplicate Unlock Code errors from BE
            setExistingUnlockCodesError(
              extractAlreadyExistsUnlockCodeError(
                data.data.couponUnlockCodeBulkCreate.errors
              )
            );
          } else {
            notify({
              status: "success",
              text: intl.formatMessage({
                defaultMessage: "Unlock Code(s) created successfully."
              })
            });
            navigate(couponUrl(couponId));
          }
        })
        .finally(() =>
          dispatchAppState({
            payload: {
              value: false
            },
            type: "displayLoader"
          })
        );
    });
  }, [
    unlockCodeState,
    couponId,
    unlockCodeCreateMutation,
    onInsertCallback,
    setExistingUnlockCodesError
  ]);

  const handleRedirectClick = useCallback(() => {
    if (navigateToUrlStr !== "") {
      navigate(navigateToUrlStr);
    } else {
      navigate(-1);
    }
  }, [navigate, navigateToUrlStr]);

  const onToggleShownWarnDialog = useCallback(
    (isShown: boolean) => () => {
      setIsDiscardWarnShown(isShown);
      if (!isShown) {
        // Clear navigateToUrlStr when closing the warning dialog
        setNavigateToUrlStr("");
      }
    },
    []
  );

  const onBreadcrumbClick = useCallback(
    (url: string) => {
      setIsDiscardWarnShown(true);
      setNavigateToUrlStr(url);
    },
    [onToggleShownWarnDialog]
  );

  const breadcrumbDirectory = useMemo(
    () => [
      {
        name: couponData?.coupon.mainPromoTitle ?? "Create coupon",
        url: couponUrl(couponId)
      }
    ],
    [couponId]
  );

  const isButtonLoading = isBulkCreateMutationLoading;

  return (
    <>
      <div className={classes.container}>
        <PageBreadcrumbNavigation
          items={breadcrumbDirectory}
          onClickCallback={onBreadcrumbClick}
        />
        <UnlockCodesHeader
          couponCode={couponData?.coupon.code ?? ""}
          unlockCodesCount={unlockCodeState.listArr.length}
          onBackClick={onToggleShownWarnDialog(true)}
        />
        <CouponUnlockCodes
          unlockCodeState={unlockCodeState}
          unlockCodeStateDispatcher={unlockCodeStateDispatcher}
          getUnlockCodeObjErrorFn={getUnlockCodeObjErrorFn}
          isDisabled={isButtonLoading}
        />
        <SaveButtonBar
          state="default"
          onCancel={onToggleShownWarnDialog(true)}
          onPublish={handleInsert}
          hideSave
          labels={SAVE_BUTTON_BAR_LABELS}
          disabled={isButtonLoading}
          disabledCancelBtn={isButtonLoading}
          disablePublishBtn={isButtonLoading}
        />
      </div>
      <DiscardWarnDialog
        isDialogShown={isDiscardWarnShown}
        onRedirect={handleRedirectClick}
        onClose={onToggleShownWarnDialog(false)}
      />
    </>
  );
};

export default CouponAddUnlockCodePage;
